/* ===========================================================
   shared/protocol.js — the live-sync wire format

   One WebSocket per open doc. Control messages are small JSON text
   frames; document data travels as binary frames:

     [type][id][index][count][payload]      (ids/counts as varuints)

   Big payloads (a pasted chapter, a whole-doc snapshot) are split
   into CHUNK-sized frames and put back together on the other end,
   so nothing ever bumps into the WebSocket message size limit.

   Binary types:
     PUSH    client → server  a batch of the client's own edits (id = batch id)
     UPDATE  server → client  an edit the server stored        (id = sequence number)
     AWARE   both ways        cursors / who is here (never stored)
     COMPACT client → server  whole-doc snapshot covering everything up to seq id
     BASE    server → client  the stored snapshot, sent before newer updates (id = its seq)
   =========================================================== */

export const T = { PUSH: 1, UPDATE: 2, AWARE: 3, COMPACT: 4, BASE: 5 };
export const CHUNK = 192 * 1024;

export const LIMITS = {
  quotaBytes: 200 * 1024 * 1024,     // per person: docs + history + images
  docBytes: 24 * 1024 * 1024,         // one doc's stored edits + history
  pushBytes: 6 * 1024 * 1024,         // one batch of edits, after reassembly
  imageBytes: 2 * 1024 * 1024,
  docsPerUser: 3000,
  titleChars: 200,
  versionNameChars: 120,
  compactRows: 300,                   // ask a client to snapshot after this many stored edits
  checkpointEvery: 20,                // every Nth saved version stores the whole doc
};

/* ---------- varuints (LEB128) ---------- */

export function writeVar(out, n) {
  while (n > 127) { out.push((n & 127) | 128); n = Math.floor(n / 128); }
  out.push(n);
}

export function readVar(buf, pos) {
  let n = 0, mult = 1, b;
  do {
    if (pos.i >= buf.length) throw new Error('truncated frame');
    b = buf[pos.i++];
    n += (b & 127) * mult;
    mult *= 128;
  } while (b & 128);
  return n;
}

/* ---------- frames ---------- */

export function frame(type, id, index, count, payload) {
  const head = [type];
  writeVar(head, id); writeVar(head, index); writeVar(head, count);
  const out = new Uint8Array(head.length + payload.length);
  out.set(head, 0);
  out.set(payload, head.length);
  return out;
}

export function parseFrame(data) {
  const buf = data instanceof Uint8Array ? data : new Uint8Array(data);
  const pos = { i: 1 };
  const type = buf[0];
  const id = readVar(buf, pos), index = readVar(buf, pos), count = readVar(buf, pos);
  return { type, id, index, count, payload: buf.subarray(pos.i) };
}

/** Split one payload into frames. */
export function framesFor(type, id, payload) {
  const count = Math.max(1, Math.ceil(payload.length / CHUNK));
  const out = [];
  for (let i = 0; i < count; i++) out.push(frame(type, id, i, count, payload.subarray(i * CHUNK, (i + 1) * CHUNK)));
  return out;
}

/** Puts chunked payloads back together. push() returns the whole payload once complete. */
export class Assembler {
  constructor(maxBytes = LIMITS.pushBytes) { this.parts = new Map(); this.maxBytes = maxBytes; }
  push(f) {
    if (f.count <= 1) return f.payload;
    const key = `${f.type}:${f.id}`;
    let p = this.parts.get(key);
    if (!p || p.count !== f.count) { p = { count: f.count, got: new Array(f.count), n: 0, bytes: 0 }; this.parts.set(key, p); }
    if (!p.got[f.index]) { p.got[f.index] = f.payload.slice(); p.n++; p.bytes += f.payload.length; }
    if (p.bytes > this.maxBytes) { this.parts.delete(key); throw new Error('too big'); }
    if (p.n < p.count) return null;
    this.parts.delete(key);
    return concat(p.got);
  }
}

export function concat(parts) {
  const len = parts.reduce((s, p) => s + p.length, 0);
  const out = new Uint8Array(len);
  let o = 0;
  for (const p of parts) { out.set(p, o); o += p.length; }
  return out;
}

/* ---------- bundles: many updates in one HTTP body ---------- */

/** [{ seq, data }] → bytes */
export function packBundle(entries) {
  const parts = [];
  const head = [];
  writeVar(head, entries.length);
  parts.push(new Uint8Array(head));
  for (const e of entries) {
    const h = [];
    writeVar(h, e.seq); writeVar(h, e.data.length);
    parts.push(new Uint8Array(h), e.data);
  }
  return concat(parts);
}

export function unpackBundle(bytes) {
  const buf = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  const pos = { i: 0 };
  const n = readVar(buf, pos);
  const out = [];
  for (let k = 0; k < n; k++) {
    const seq = readVar(buf, pos), len = readVar(buf, pos);
    out.push({ seq, data: buf.subarray(pos.i, pos.i + len) });
    pos.i += len;
  }
  return out;
}

/* ---------- ids ---------- */

const ALPHA = '23456789abcdefghijkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ';
export function newId(len = 16) {
  const bytes = crypto.getRandomValues(new Uint8Array(len));
  let s = '';
  for (const b of bytes) s += ALPHA[b % ALPHA.length];
  return s;
}
export const isId = s => typeof s === 'string' && /^[23456789a-km-zA-HJ-NP-Z]{12,32}$/.test(s);
